import { c as _c } from "react/compiler-runtime";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PrV2ChapterEntry } from './lib/pr-v2-chapter-mode';
import { cn } from '@/lib/utils';
type PrV2ChapterSwitcherProps = {
  chapterEntries: PrV2ChapterEntry[];
  activeChapterKey: string | null;
  placement: 'top' | 'bottom';
  onSelectChapter: (chapterKey: string) => void;
};
export function PrV2ChapterSwitcher(t0) {
  const $ = _c(53);
  const {
    chapterEntries,
    activeChapterKey,
    placement,
    onSelectChapter
  } = t0;
  let t1;
  if ($[0] !== activeChapterKey || $[1] !== chapterEntries) {
    let t2;
    if ($[3] !== activeChapterKey) {
      t2 = entry => entry.key === activeChapterKey;
      $[3] = activeChapterKey;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = chapterEntries.findIndex(t2);
    $[0] = activeChapterKey;
    $[1] = chapterEntries;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeIndex = Math.max(0, t1);
  const activeEntry = chapterEntries[activeIndex] ?? chapterEntries[0] ?? null;
  if (!activeEntry) {
    return null;
  }
  const hasPrevious = activeIndex > 0;
  const hasNext = activeIndex < chapterEntries.length - 1;
  const showTextLabels = placement === "bottom";
  const t2 = `pr-v2-chapter-switcher-${placement}`;
  const t3 = placement === "bottom" && "border-t border-zinc-900 pt-4";
  let t4;
  if ($[5] !== t3) {
    t4 = cn("flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-400", t3);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = `Group ${activeIndex + 1} of ${chapterEntries.length}`;
  let t6;
  if ($[7] !== t5) {
    t6 = <span className="shrink-0 text-[11px] font-medium uppercase text-zinc-500">{t5}</span>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== activeEntry || $[10] !== placement) {
    t7 = placement === "bottom" ? <span data-testid="pr-v2-chapter-switcher-title-bottom" className="min-w-0 truncate font-medium text-zinc-200">{activeEntry.group.groupName}</span> : null;
    $[9] = activeEntry;
    $[10] = placement;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className="flex min-w-0 items-baseline gap-2">{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = `pr-v2-chapter-previous-button-${placement}`;
  const t10 = hasPrevious ? "hover:bg-zinc-900 hover:text-zinc-100" : "cursor-not-allowed opacity-35";
  let t11;
  if ($[15] !== t10) {
    t11 = cn("inline-flex h-7 items-center justify-center gap-1 rounded px-2 text-zinc-400", t10);
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = !hasPrevious;
  let t13;
  if ($[17] !== activeIndex || $[18] !== chapterEntries || $[19] !== onSelectChapter) {
    t13 = () => {
      const previousEntry = chapterEntries[activeIndex - 1];
      if (previousEntry) {
        onSelectChapter(previousEntry.key);
      }
    };
    $[17] = activeIndex;
    $[18] = chapterEntries;
    $[19] = onSelectChapter;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronLeft className="size-3.5" aria-hidden="true" />;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== showTextLabels) {
    t15 = showTextLabels ? <span>Previous</span> : null;
    $[22] = showTextLabels;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t11 || $[25] !== t12 || $[26] !== t13 || $[27] !== t15 || $[28] !== t9) {
    t16 = <button type="button" data-testid={t9} aria-label="Previous group" className={t11} disabled={t12} onClick={t13}>{t14}{t15}</button>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t15;
    $[28] = t9;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = `pr-v2-chapter-next-button-${placement}`;
  const t18 = hasNext ? "hover:bg-zinc-900 hover:text-zinc-100" : "cursor-not-allowed opacity-35";
  let t19;
  if ($[30] !== t18) {
    t19 = cn("inline-flex h-7 items-center justify-center gap-1 rounded px-2 text-zinc-400", t18);
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const t20 = !hasNext;
  let t21;
  if ($[32] !== activeIndex || $[33] !== chapterEntries || $[34] !== onSelectChapter) {
    t21 = () => {
      const nextEntry = chapterEntries[activeIndex + 1];
      if (nextEntry) {
        onSelectChapter(nextEntry.key);
      }
    };
    $[32] = activeIndex;
    $[33] = chapterEntries;
    $[34] = onSelectChapter;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== showTextLabels) {
    t22 = showTextLabels ? <span>Next</span> : null;
    $[36] = showTextLabels;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ChevronRight className="size-3.5" aria-hidden="true" />;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== t17 || $[40] !== t19 || $[41] !== t20 || $[42] !== t21 || $[43] !== t22) {
    t24 = <button type="button" data-testid={t17} aria-label="Next group" className={t19} disabled={t20} onClick={t21}>{t22}{t23}</button>;
    $[39] = t17;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t16 || $[46] !== t24) {
    t25 = <div className="flex shrink-0 items-center gap-1">{t16}{t24}</div>;
    $[45] = t16;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t2 || $[49] !== t25 || $[50] !== t4 || $[51] !== t8) {
    t26 = <div data-testid={t2} className={t4}>{t8}{t25}</div>;
    $[48] = t2;
    $[49] = t25;
    $[50] = t4;
    $[51] = t8;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  return t26;
}